<template>
  <FlotoCrudContainer
    as-table
    prepend-new-item
    :fetch-fn="getAllocations"
    :delete-fn="deleteAllocation"
    :create-fn="addAllocation"
    :update-fn="updateAllocation"
    :columns="tableColumns"
    :default-item="{ status: 'in_use' }"
    :scrollable="false"
    :show-no-data="false"
  >
    <template v-slot:add-controls="{ create, refreshList }">
      <MRow class="mb-4" :gutter="0">
        <MCol :size="12" class="m-align-button">
          <span class="mx-1">
            {{ $tc('available') }} {{ $tc('quantity') }}:
            {{ resource.availableQuantity }}
          </span>
          <MButton
            v-if="!disabled"
            variant="primary"
            :disabled="resource.availableQuantity <= 0"
            @click="create"
          >
            {{ $tc('allocate') }}
          </MButton>
        </MCol>
      </MRow>
    </template>
    <template v-slot:form-header="{ item }">
      {{ $tc('allocate') }} {{ $tc('quantity') }}
    </template>
    <template v-slot:form-items="{ item: allocation }">
      <AllocationForm :form-data="allocation" :resource="resource" />
    </template>
    <template v-slot:form-actions="{ submit, cancel, item, processing }">
      <MButton
        id="update-add-btn"
        class="mx-1"
        :loading="processing"
        @click="submit"
      >
        {{ $tc(item.id ? 'update' : 'add') }}
      </MButton>
      <MButton id="cancel-btn" variant="default" @click="cancel">
        {{ $tc('cancel') }}
      </MButton>
    </template>
    <template v-slot:status="{ item, edit }">
      <td class="text-ellipsis">
        {{
          (allocationStatusOptions.find((o) => o.key === item.status) || {})
            .text || '---'
        }}
      </td>
    </template>
    <template v-slot:createdAt="{ item }">
      <td class="text-ellipsis">
        {{ item.createdAt | datetime }}
      </td>
    </template>
    <template v-slot:quantity="{ item }">
      <td class="text-ellipsis">
        {{ item.quantity }}
      </td>
    </template>
    <template v-slot:allocatedUserId="{ item }">
      <td class="text-ellipsis">
        <FlotoUserDrawer
          v-if="item.allocatedUserId"
          :user-id="item.allocatedUserId"
        />
        <span v-else>---</span>
      </td>
    </template>
    <template v-slot:locationId="{ item }">
      <td class="text-ellipsis">
        <FlotoLocationPicker
          placeholder="---"
          text-only
          :value="item.locationId"
          disabled
        />
      </td>
    </template>
    <template v-slot:departmentId="{ item }">
      <td class="text-ellipsis">
        <FlotoDepartmentPicker
          placeholder="---"
          text-only
          :value="item.departmentId"
          disabled
        />
      </td>
    </template>
    <template v-slot:assetId="{ item }">
      <td class="text-ellipsis">
        <AssetPickerDrawer
          clickable
          :module-name="$constants.ASSET_HARDWARE"
          placeholder="---"
          :value="item.assetId"
          disabled
        />
      </td>
    </template>
    <template v-slot:description="{ item }">
      <td class="text-ellipsis">
        {{ item.description || '---' }}
      </td>
    </template>
    <template v-slot:actions="{ item, remove, edit }">
      <td class="text-ellipsis">
        <MTooltip>
          <template v-slot:trigger>
            <a :disabled="disabled" @click="edit">
              <MIcon name="pencil" class="cursor-pointer text-primary" />
            </a>
          </template>
          {{ $t('edit') }}
        </MTooltip>

        <FlotoDeleteBtn
          class="mx-2"
          :disabled="disabled"
          :class="{ invisible: !item.canDelete }"
          :message="$t('confirm_delete_item', { item: $tc('quantity') })"
          @confirm="remove"
        />
      </td>
    </template>
  </FlotoCrudContainer>
</template>

<script>
import AssetPickerDrawer from '@components/data-picker/asset-picker-drawer'
import AllocationForm from './allocation-form'
import {
  getAllocationsApi,
  bulkDeleteApi,
  updateAllocationApi,
  createAllocationApi,
} from '../../api/allocation-api'
import { allocationStatusOptions } from '../../helpers/option-map'
export default {
  name: 'AllocationList',
  components: { AllocationForm, AssetPickerDrawer },
  props: {
    resourceId: { type: Number, required: true },
    resource: {
      type: Object,
      default() {
        return {}
      },
    },
    disabled: { type: Boolean, default: false },
  },
  data() {
    this.allocationStatusOptions = allocationStatusOptions()
    return {
      searchTerm: '',
      tableColumns: [
        { name: `${this.$tc('issued_date')}`, key: 'createdAt' },
        { name: `${this.$tc('status')}`, key: 'status', width: '100px' },
        {
          name: `${this.$tc('quantity')}`,
          key: 'quantity',
          width: '50px',
        },
        {
          name: `${this.$tc('allocate')} ${this.$tc('to')}`,
          key: 'allocatedUserId',
        },
        {
          name: `${this.$tc('location')}`,
          key: 'locationId',
        },
        {
          name: `${this.$tc('department')}`,
          key: 'departmentId',
        },
        {
          name: `${this.$tc('asset')}`,
          key: 'assetId',
        },
        {
          name: `${this.$tc('description')}`,
          key: 'description',
        },
        {
          name: `${this.$tc('action', 2)}`,
          key: 'actions',
          width: '120px',
        },
      ],
    }
  },
  methods: {
    getAllocations(limit, offset) {
      return getAllocationsApi(this.resourceId, limit, offset)
    },
    deleteAllocation({ id }) {
      return bulkDeleteApi(this.resourceId, [id]).then((data) => {
        this.$emit('refresh', ['allocation'])
        return data
      })
    },
    bulkDelete(ids) {
      return bulkDeleteApi(this.resourceId, ids).then((data) => {
        this.$emit('refresh', ['allocation'])
        return data
      })
    },
    updateAllocation(data) {
      return updateAllocationApi(this.resourceId, data).then((data) => {
        this.$emit('refresh', ['allocation'])
        return data
      })
    },
    addAllocation(data) {
      const requestData = { ...data }
      return createAllocationApi(this.resourceId, requestData).then((data) => {
        this.$emit('refresh', ['allocation'])
        return data
      })
    },
  },
}
</script>
